import type { JssStyle } from 'jss';
import type { Observable } from 'rxjs';
import type { UntypedFormControl } from '@angular/forms';
import type { WidgetErrors } from '@common/types/widget-errors.type';
import type { SelectedFileExtended } from '@common/types/file.type';
import type { WidgetsDataTypes } from '@common/enums';
import type { WebWidgetData, WebLegacyWidgetEntity } from '@web/types';

import { Directive, inject, Input } from '@angular/core';
import { debounceTime, filter, skip, takeUntil } from 'rxjs/operators';

import { SpBaseControlComponent } from '@common/abstract/sp-base-control.component';

import { WebWidgetsService } from '@web/core/services/web-widgets.service';
import { ImageEditorService } from '@web/shared/modals/image-editor-modal/image-editor.service';

import { AppFacade } from '@web/store/app/app.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { WidgetsErrorsFacade } from '@web/store/page-structure/widgets-errors/widgets-errors.facade';
import { HistoryFacade } from '@web/store/history/history.facade';
import { UserFacade } from '@web/store/user/user.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';

@Directive()
export abstract class WebBaseControlComponent<T extends WebWidgetData> extends SpBaseControlComponent<
    T,
    WebLegacyWidgetEntity
> {
    @Input() public isSpecific: boolean = false;
    @Input() public isEduSite!: boolean;

    private readonly widgetsService: WebWidgetsService = inject(WebWidgetsService);
    private readonly imageEditorService: ImageEditorService = inject(ImageEditorService);

    protected readonly appFacade: AppFacade = inject(AppFacade);
    protected readonly widgetsFacade: WidgetsFacade = inject(WidgetsFacade);
    protected readonly widgetsErrorsFacade: WidgetsErrorsFacade = inject(WidgetsErrorsFacade);
    protected readonly historyFacade: HistoryFacade = inject(HistoryFacade);
    protected readonly userFacade: UserFacade = inject(UserFacade);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);

    protected skipHistory: boolean = false;

    public readonly isDeviceTablet$: Observable<boolean> = this.layoutFacade.isDeviceTablet$;
    public readonly isDeviceMobile$: Observable<boolean> = this.layoutFacade.isDeviceMobile$;
    public readonly isDeviceDesktop$: Observable<boolean> = this.layoutFacade.isDeviceDesktop$;

    protected initFormSubscription(): void {
        this.controlForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((data) => {
            const changes: Pick<WebLegacyWidgetEntity, 'data' | 'styles' | 'mobileStyles'> = { data: this.widget.data };
            if (data.styles) {
                changes.styles = this.widget.styles;
            }

            if (data.mobileStyles) {
                changes.mobileStyles = this.widget.mobileStyles;
            }

            if (!this.skipHistory) {
                this.historyFacade.addWidgetUpdateChangeToHistory(
                    { id: this.widget.id, changes },
                    {
                        id: this.widget.id,
                        changes: data,
                    }
                );
            }
            this.updateWidgetProperties(data);
            this.skipHistory = false;
        });

        this.widget$
            .pipe(
                skip(1),
                debounceTime(200),
                takeUntil(this.unsubscribe$),
                filter((widget: WebLegacyWidgetEntity) => Boolean(widget))
            )
            .subscribe(() => {
                this.updateWidgetErrors(this.getWidgetValidationErrors());
            });
    }

    protected updateWidgetData(data: Partial<T>): void {
        this.widgetsFacade.updateWidgetData(this.widget.id, data);
    }

    protected updateWidgetDisplayId(displayId: string): void {
        this.widgetsFacade.updateWidgetDisplayId(this.widget.id, displayId);
    }

    protected updateWidgetStyles(styles: JssStyle): void {
        this.widgetsFacade.updateWidgetStyles(this.widget.id, styles);
    }

    protected updateWidgetProperties(changes: Partial<WebLegacyWidgetEntity<T>>): void {
        this.widgetsFacade.updateLegacyWidgetProperties(this.widget.id, changes);
    }

    protected updateWidgetErrors(errors: WidgetErrors): void {
        if (!errors) {
            return;
        }

        this.widgetsErrorsFacade.updateWidgetsErrors(this.widget.id, {
            label: this.translateService.instant(this.widgetsService.getWidgetModel(this.widget.name)?.label),
            errors,
        });
    }

    protected showImageEditor(image: SelectedFileExtended, dataType: WidgetsDataTypes, index?: number) {
        this.imageEditorService.showEditImageModal(image, this.widget, dataType, index);
    }

    public setSkipHistory(): void {
        this.skipHistory = true;
    }

    public get data(): UntypedFormControl {
        return this.controlForm.get('data') as UntypedFormControl;
    }

    public get styles(): UntypedFormControl {
        return this.controlForm.get('styles') as UntypedFormControl;
    }

    public get mobileStyles(): UntypedFormControl {
        return this.controlForm.get('mobileStyles') as UntypedFormControl;
    }

    public get displayId(): string {
        return this.widget.displayId ? this.widget.displayId : `widget-${this.widget.id}`;
    }

    public get isZeroWidget(): boolean {
        return this.widget?.data?.widgetType === 'zero';
    }
}
